// @owners { team: patient-health }
import { SPACING } from '@alto/design-library-tokens';
import { MdPadding, useScreenSize } from '@alto/design-system';
import { BackorderedAlert, useBackorderedMedication } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import { FlexColumn, FlexRow, H2, palette, spacing } from '@alto/pocky';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { type RefObject, useState } from 'react';
import styled from 'styled-components';
import { type Video } from '~shared/features/instructional-videos/types';
import {
  MULTIPLE_ACTIVE_RX,
  MULTIPLE_ACTIVE_RX_READY,
  MULTIPLE_PRICES,
  RX_READY,
  SCHEDULED,
} from '~shared/features/my-meds/constants';
import { getInstructionalVideosForMedication } from '~shared/features/my-meds/helper';
import { type Medication, type MedicationTagKey } from '~shared/features/my-meds/types';
import { getNoAvailableDates } from '~shared/features/next-available-date/helpers/getNoAvailableDates';
import { getNextAvailableDatesByPrescriptionID } from '~shared/features/next-available-date/selectors/getNextAvailableDatesByPrescriptionID';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { type Event } from '~shared/lib/analytics/src/types';
import { useSelectorShared } from '~shared/store';
import { type DeliveryDetails, type Prescription as PrescriptionT } from '~shared/types/clients';
import { ADD_TO_CART_DISPLAY } from './AddToCart';
import PrescriptionAction from './PrescriptionActionContainer';
import MedicationImageLegacy, { type MedicationImageLegacyProps } from '~web/components/MedicationImageLegacy';
import { UpcomingDeliveryCard } from '~web/components/UpcomingDeliveryCard';
import { Infobox } from '~web/features/my-meds/components/Infobox';
import { MedListDetail } from '~web/features/my-meds/components/med-list/MedListDetail';
import { useTrackNodeSeen } from '~web/hooks';

type Props = {
  readonly activePrescriptions: PrescriptionT[];
  readonly initShowDetail?: boolean;
  readonly medication: Medication;
  readonly includeFertVideos?: boolean;
  readonly onClickVideoIcon?: (video: Video) => void;
  readonly tagKey: MedicationTagKey | null;
  readonly tagData: Record<string, unknown>;
  readonly sendAnalyticsEvent: (event: Event) => void;
  readonly upcomingShipmentID?: number;
  readonly upcomingDeliveryDetails?: DeliveryDetails;
};
export const MedListRowContainer = styled(FlexRow)<{ short?: boolean; isDetailShowing?: boolean }>`
  background-color: ${palette.altoColorsW};
  border: 1px solid ${palette.grey2};
  padding: ${({ short }) => (short ? spacing.smSpacing : spacing.mdSpacing)} ${spacing.mdSpacing};
  &:not(:last-child) {
    border-bottom: none;
  }
`;

const FlexColumnWithPointer = styled(FlexColumn)<{ isLGScreenOrBigger: boolean }>`
  cursor: pointer;
  ${({ isLGScreenOrBigger }) => isLGScreenOrBigger && `justify-content: space-between;`}
`;

const MedImage = styled(MedicationImageLegacy)<MedicationImageLegacyProps>`
  height: 3.5rem;
  width: 4.75rem;
`;

const Circle = styled.div<{ isRowHovered: boolean }>`
  background-color: ${({ isRowHovered }) => {
    if (isRowHovered) {
      return palette.navyTint2;
    }

    return 'transparent';
  }};
  border: ${spacing.borderWidth} solid ${palette.grey1};
  border-radius: 50%;
  height: ${spacing.lgSpacing};
  width: ${spacing.lgSpacing};
  cursor: pointer;
`;

const AnchoredAction = styled(FlexRow)`
  justify-content: flex-end;
`;

const SpacedColumn = styled(FlexColumn)<{ isXLScreenOrBigger: boolean }>`
  justify-content: space-between;
  ${({ isXLScreenOrBigger }) => isXLScreenOrBigger && `flex-wrap: nowrap;`}
`;

const FlexColumnNoWrapLG = styled(FlexColumn)<{ isLGScreenOrBigger: boolean }>`
  ${({ isLGScreenOrBigger }) => isLGScreenOrBigger && `flex-wrap: nowrap; padding-right: ${SPACING.STATIC.MD.px};`}
`;

const MedListRow = ({
  activePrescriptions,
  medication,
  includeFertVideos,
  onClickVideoIcon,
  initShowDetail,
  tagKey,
  tagData,
  sendAnalyticsEvent,
  upcomingShipmentID,
  upcomingDeliveryDetails,
}: Props) => {
  const [isRowHovered, setIsRowHovered] = useState(false);
  const { isLGScreenOrBigger, isXLScreenOrBigger } = useScreenSize();
  const [isDetailShowing, setIsDetailShowing] = useState(initShowDetail);
  const availabilityData = useSelectorShared((state) => getNextAvailableDatesByPrescriptionID(state));
  const prescriptionId = medication.representativePrescription.id;
  const isBackorderdMedication = useBackorderedMedication(prescriptionId);

  const toggleDetail = () => {
    setIsDetailShowing(!isDetailShowing);
  };

  const onMouseEnterRow = () => {
    setIsRowHovered(true);
  };

  const onMouseLeaveRow = () => {
    setIsRowHovered(false);
  };

  const { medicationName, representativePrescription } = medication;
  const hasSingleActivePrescription = activePrescriptions.length === 1;
  const instructionalVideos = getInstructionalVideosForMedication(medication);

  const shouldDisplayUpcomingDeliveryDetails = tagKey === SCHEDULED;

  const refNode = useTrackNodeSeen({
    onSeen: () => {
      if (tagKey === MULTIPLE_ACTIVE_RX_READY || tagKey === MULTIPLE_ACTIVE_RX) {
        const rxs = tagKey === MULTIPLE_ACTIVE_RX ? tagData['active rxs'] : tagData['schedulable rxs'];
        // @ts-expect-error TS(7006): Parameter 'rx' implicitly has an 'any' type.
        rxs.forEach((rx) => {
          const nad = availabilityData[rx.id];
          const noAvailableDates = getNoAvailableDates(nad);
          const noAvailableDatesReasons = noAvailableDates
            ? `${nad.earliest.reason}, ${nad.lastAvailableDate.reason}`
            : '';
          sendAnalyticsEvent(
            createEvent(
              EVENTS.MED_LIST__ROW_VIEWED,
              {
                'is prescription scheduleable': tagKey === MULTIPLE_ACTIVE_RX_READY,
                'multiple active rxs': true,
                'has ancillaries': !!rx.has_ancillary_items,
                'no available dates': noAvailableDates,
                'no available dates reasons (earliest, latest)': noAvailableDatesReasons,
              },
              { prescriptionId: rx.id },
            ),
          );
        });
      } else {
        const nad = availabilityData[representativePrescription.id];
        const noAvailableDates = getNoAvailableDates(nad);
        sendAnalyticsEvent(
          createEvent(
            EVENTS.MED_LIST__ROW_VIEWED,
            {
              'is prescription scheduleable': tagKey === RX_READY || tagKey === MULTIPLE_PRICES,
              'multiple active rxs': false,
              'has ancillaries': !!representativePrescription.has_ancillary_items,
              'no available dates': noAvailableDates,
            },
            { prescriptionId: representativePrescription.id },
          ),
        );
      }
    },
  });

  return (
    <MedListRowContainer
      data-testid="MedListRow"
      isDetailShowing={isDetailShowing}
      key={medicationName}
      spacing={spacing.mdSpacing}
      ref={refNode as RefObject<HTMLDivElement>}
    >
      <SpacedColumn
        isXLScreenOrBigger={isXLScreenOrBigger}
        wrap
      >
        <FlexColumnNoWrapLG
          wrap
          spacing={spacing.smSpacing}
          growToParent
          isLGScreenOrBigger={isLGScreenOrBigger}
        >
          <MedImage
            imageUrl={representativePrescription.image_url}
            hasVideos={(includeFertVideos && !!instructionalVideos.length) ?? false}
            onClickVideoIcon={() => {
              if (onClickVideoIcon) {
                // if multiple videos for this medication, just pass in the first arbitrarily
                onClickVideoIcon(instructionalVideos[0]);
              }
            }}
          />
          <FlexRow
            spacing={spacing.xxsSpacing}
            leftAlignContent={false}
            growToParent
          >
            <FlexColumnWithPointer
              spacing={spacing.smSpacing}
              onClick={toggleDetail}
              onMouseEnter={onMouseEnterRow}
              onMouseLeave={onMouseLeaveRow}
              isLGScreenOrBigger={isLGScreenOrBigger}
            >
              <H2>{medicationName}</H2>
              <FlexRow
                verticallyAlignContent
                noShrink
                className="fa-stack"
              >
                <Circle
                  className="fa-stack-1x"
                  isRowHovered={isRowHovered}
                />
                <FontAwesomeIcon
                  className="fa-stack-1x"
                  icon={isDetailShowing ? faChevronUp : faChevronDown}
                  color={palette.grey1}
                  size="sm"
                />
              </FlexRow>
            </FlexColumnWithPointer>
            {isBackorderdMedication ? (
              <MdPadding
                leftPadding={SPACING.STATIC.NONE}
                rightPadding={SPACING.STATIC.NONE}
              >
                <BackorderedAlert
                  origin="Med List"
                  prescriptionId={prescriptionId}
                />
              </MdPadding>
            ) : null}
            <Infobox medication={medication} />
            {shouldDisplayUpcomingDeliveryDetails && upcomingShipmentID ? (
              <UpcomingDeliveryCard
                shipmentID={upcomingShipmentID}
                origin={ORIGIN_NAMES.MED_LIST}
                deliveryDetails={upcomingDeliveryDetails}
                isEditable
              />
            ) : null}

            {/* contains <AddToCart> which includes NAD, etc */}
            {hasSingleActivePrescription ? (
              <PrescriptionAction
                showAddToCartOnly={ADD_TO_CART_DISPLAY.INFO_ONLY}
                prescription={activePrescriptions[0]}
              />
            ) : null}
          </FlexRow>
        </FlexColumnNoWrapLG>
        {hasSingleActivePrescription ? (
          <AnchoredAction>
            <PrescriptionAction
              showAddToCartOnly={ADD_TO_CART_DISPLAY.ACTION_ONLY}
              prescription={activePrescriptions[0]}
            />
          </AnchoredAction>
        ) : null}
      </SpacedColumn>

      {isDetailShowing ? (
        <MedListDetail
          medication={medication}
          suppressAction={hasSingleActivePrescription}
        />
      ) : null}
    </MedListRowContainer>
  );
};

export default MedListRow;
